// Google auth + API helpers — refresh-token flow.
// Sign in ONCE; a server-side refresh keeps the session alive forever.

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const REDIRECT_URI = 'https://app.mullerhomesllc.com/api/google-callback';
const SCOPES = [
  'https://www.googleapis.com/auth/calendar.readonly',
  'https://www.googleapis.com/auth/spreadsheets',
  'https://www.googleapis.com/auth/drive',
  'https://www.googleapis.com/auth/gmail.readonly',
].join(' ');

const TOKEN_KEY = 'google_access_token';
const TOKEN_EXP_KEY = 'google_token_expires_at';
const REFRESH_KEY = 'google_refresh_token';

let refreshTimer = null;

// ── Catch tokens arriving from /api/google-callback (runs on app load) ──
(function captureAuthRedirect() {
  try {
    if (typeof window === 'undefined') return;
    const m = window.location.hash.match(/gt=([A-Za-z0-9_-]+)/);
    if (!m) return;
    const json = JSON.parse(atob(m[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (json.at) {
      localStorage.setItem(TOKEN_KEY, json.at);
      localStorage.setItem(TOKEN_EXP_KEY, String(json.exp || Date.now() + 3600000));
      if (json.rt) localStorage.setItem(REFRESH_KEY, json.rt);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
  } catch (e) { console.error('Auth redirect parse error:', e); }
})();

// ── Public: token access ──────────────────────────────────
export function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

function tokenIsFresh() {
  const exp = parseInt(localStorage.getItem(TOKEN_EXP_KEY) || '0');
  return Date.now() < exp - 5 * 60 * 1000; // stale 5 min early
}

async function refreshAccessToken() {
  const rt = localStorage.getItem(REFRESH_KEY);
  if (!rt) return null;
  try {
    const res = await fetch('/api/google-refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: rt }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.access_token) return null;
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(TOKEN_EXP_KEY, String(Date.now() + (data.expires_in || 3600) * 1000));
    return data.access_token;
  } catch (e) { return null; }
}

// Guaranteed-fresh token, zero prompts (as long as a refresh token exists)
export async function ensureToken() {
  const stored = getStoredToken();
  if (stored && tokenIsFresh()) return stored;
  const refreshed = await refreshAccessToken();
  return refreshed || stored || null;
}

// First-time connect — full-page redirect to Google, once
export async function getGoogleToken() {
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    response_type: 'code',
    scope: SCOPES,
    access_type: 'offline',
    prompt: 'consent',
  });
  window.location.href = 'https://accounts.google.com/o/oauth2/v2/auth?' + params.toString();
  return new Promise(() => {}); // page navigates away
}

// Background refresh every 45 minutes + immediately if stale
export function startAutoRefresh() {
  if (refreshTimer) clearInterval(refreshTimer);
  refreshTimer = setInterval(() => { refreshAccessToken(); }, 45 * 60 * 1000);
  if (!tokenIsFresh()) refreshAccessToken();
}

// ── Calendar ──────────────────────────────────────────────
export async function fetchCalendarEvents(calendarId, days = 14) {
  const token = await ensureToken();
  if (!token) return [];
  const timeMin = new Date().toISOString();
  const timeMax = new Date(Date.now() + days * 86400000).toISOString();
  const url =
    `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events` +
    `?timeMin=${encodeURIComponent(timeMin)}&timeMax=${encodeURIComponent(timeMax)}` +
    `&singleEvents=true&orderBy=startTime&maxResults=100`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Calendar fetch failed: ${res.status}`);
  const data = await res.json();
  return data.items || [];
}

// ── Drive upload ──────────────────────────────────────────
export async function uploadToDrive(file, folderId, filename) {
  const token = await ensureToken();
  if (!token) throw new Error('No Google token');
  const metadata = {
    name: filename || file.name,
    parents: folderId ? [folderId] : undefined,
  };
  const form = new FormData();
  form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
  form.append('file', file);
  const res = await fetch(
    'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,webViewLink',
    { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form }
  );
  if (!res.ok) throw new Error(`Drive upload failed: ${res.status}`);
  return res.json();
}

// ── Compatibility with previous google.js exports ─────────
export const loadGoogleAuth = () => Promise.resolve(null);
export const loadGapi = () => Promise.resolve(null);

// ── Drive listing (used by Media tab) ─────────────────────
export async function listDriveFiles(folderId, maxResults = 100) {
  const token = await ensureToken();
  if (!token) return [];
  const q = encodeURIComponent(`'${folderId}' in parents and trashed = false`);
  const url =
    `https://www.googleapis.com/drive/v3/files?q=${q}` +
    `&fields=files(id,name,mimeType,thumbnailLink,webViewLink,createdTime)` +
    `&orderBy=createdTime desc&pageSize=${maxResults}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`Drive list failed: ${res.status}`);
  const data = await res.json();
  return data.files || [];
}

// ── Drive folder + sheet helpers ──────────────────────────
export async function createDriveFolder(name, parentId) {
  const token = await ensureToken();
  if (!token) throw new Error('No Google token');
  const res = await fetch('https://www.googleapis.com/drive/v3/files?fields=id,name,webViewLink', {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name,
      mimeType: 'application/vnd.google-apps.folder',
      parents: parentId ? [parentId] : undefined,
    }),
  });
  if (!res.ok) throw new Error(`Drive folder create failed: ${res.status}`);
  return res.json();
}

export async function fetchSheetValues(spreadsheetId, range) {
  const token = await ensureToken();
  if (!token) return null;
  const res = await fetch(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(range)}?majorDimension=ROWS`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) throw new Error(`Sheet fetch failed: ${res.status}`);
  const data = await res.json();
  return data.values || [];
}

// ── Sheets write helpers ──────────────────────────────────
export async function appendToSheet(spreadsheetId, range, values) {
  const token = await ensureToken();
  if (!token) throw new Error('No Google token');
  const res = await fetch(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(range)}:append?valueInputOption=USER_ENTERED&insertDataOption=INSERT_ROWS`,
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ values: Array.isArray(values[0]) ? values : [values] }),
    }
  );
  if (!res.ok) throw new Error(`Sheet append failed: ${res.status}`);
  return res.json();
}

export async function updateSheetValues(spreadsheetId, range, values) {
  const token = await ensureToken();
  if (!token) throw new Error('No Google token');
  const res = await fetch(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/${encodeURIComponent(range)}?valueInputOption=USER_ENTERED`,
    {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ values: Array.isArray(values[0]) ? values : [values] }),
    }
  );
  if (!res.ok) throw new Error(`Sheet update failed: ${res.status}`);
  return res.json();
}

export async function getSheetMeta(spreadsheetId) {
  const token = await ensureToken();
  if (!token) return null;
  const res = await fetch(
    `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}?fields=sheets.properties`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) throw new Error(`Sheet meta failed: ${res.status}`);
  return res.json();
}

export async function deleteDriveFile(fileId) {
  const token = await ensureToken();
  if (!token) throw new Error('No Google token');
  const res = await fetch(`https://www.googleapis.com/drive/v3/files/${fileId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.ok;
}

export async function getDriveFile(fileId) {
  const token = await ensureToken();
  if (!token) return null;
  const res = await fetch(
    `https://www.googleapis.com/drive/v3/files/${fileId}?fields=id,name,mimeType,thumbnailLink,webViewLink,createdTime`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) throw new Error(`Drive get failed: ${res.status}`);
  return res.json();
}
